import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface TrialPersonalFields {
  first_name: string; last_name: string; phone: string; email: string; age: string;
  gender: "male" | "female" | ""; height_cm: string; weight_kg: string;
  clothing_size: string; emergency_phone: string;
}
interface Props {
  value: TrialPersonalFields;
  onChange: <K extends keyof TrialPersonalFields>(key: K, value: TrialPersonalFields[K]) => void;
  t: (key: string) => string;
}

export function PublicTrialPersonalFields({ value, onChange, t }: Props) {
  const field = (key: "first_name" | "last_name" | "phone" | "email" | "age" | "height_cm" | "weight_kg" | "emergency_phone", label: string, type = "text", required = true, placeholder?: string) => (
    <div className={cn("min-w-0 space-y-1.5", key === "emergency_phone" && "trial-emergency-field")}>
      <Label htmlFor={key}>{t(label)} {required ? "*" : `(${t("optional")})`}</Label>
      <Input id={key} type={type} required={required} value={value[key]} placeholder={placeholder}
        autoComplete={key === "first_name" ? "given-name" : key === "last_name" ? "family-name" : key === "phone" ? "tel" : key === "email" ? "email" : "off"}
        maxLength={type === "tel" ? 30 : key === "email" ? 255 : 60}
        min={key === "age" ? 10 : key === "height_cm" ? 100 : key === "weight_kg" ? 30 : undefined}
        max={key === "age" ? 100 : key === "height_cm" ? 250 : key === "weight_kg" ? 300 : undefined}
        step={key === "weight_kg" || key === "height_cm" ? "any" : undefined}
        onChange={event => onChange(key, event.target.value)} />
    </div>
  );
  return <div className="trial-personal-grid">
    {field("first_name", "fields.firstName", "text", true, t("placeholders.firstName"))}
    {field("last_name", "fields.lastName", "text", true, t("placeholders.lastName"))}
    {field("phone", "fields.mobile", "tel", true, t("placeholders.phone"))}
    {field("email", "fields.email", "email", false, t("placeholders.email"))}
    {field("age", "fields.age", "number", true, t("placeholders.age"))}
    <div className="min-w-0 space-y-1.5"><Label id="gender-label">{t("fields.gender")} *</Label><div role="group" aria-labelledby="gender-label" className="grid grid-cols-2 gap-2">
      {(["male", "female"] as const).map(gender => <Button key={gender} type="button" variant="outline" aria-pressed={value.gender === gender}
        className={cn("h-9 px-2 text-xs", value.gender === gender && "border-primary bg-primary/15 text-primary")}
        onClick={() => onChange("gender", gender)}>{t(`fields.${gender}`)}</Button>)}
    </div></div>
    {field("height_cm", "fields.height", "number", true, "170")}
    {field("weight_kg", "fields.weight", "number", true, "70")}
    <div className="min-w-0 space-y-1.5"><Label id="size-label">{t("fields.clothingSize")} *</Label><div role="group" aria-labelledby="size-label" className="grid grid-cols-6 gap-1">
      {["XS", "S", "M", "L", "XL", "XXL"].map(size => <Button key={size} type="button" variant="outline" aria-pressed={value.clothing_size === size}
        className={cn("h-9 px-0 text-xs", value.clothing_size === size && "border-primary bg-primary/15 text-primary")}
        onClick={() => onChange("clothing_size", size)}>{size}</Button>)}
    </div></div>
    {field("emergency_phone", "fields.emergencyPhone", "tel", true, t("placeholders.emergencyPhone"))}
  </div>;
}